import * as React from 'react';
import {
  AutomationAction,
  AUTOMATION_ACTION_TYPE,
} from '../../../../MedicareProAPI/Entities/Automation/AutomationAction';
import {
  AUTOMATION_TRIGGER_RECIPE_LABELS,
  AUTOMATION_ACTION_TYPE_LABELS,
  AUTOMATION_ACTION_TYPE_ICONS,
} from '../../../Language/en';
import { Utilities } from '../../../../MedicareProAPI/Utilities/Utilities';
import { AutomationActionFormAction } from '../AutomationActionFormAction';
import { AutomationActionFormTrigger } from '../AutomationActionFormTrigger';
import { SelectMap } from '../../../Utilities/SelectMap';
import {
  AUTOMATION_ACTION_TRIGGER_TYPE,
  AUTOMATION_TRIGGER_COMBINATION,
  AUTOMATION_TRIGGER_DATA_FIELD_TYPE,
  AutomationActionTriggerData,
} from '../../../../MedicareProAPI/Entities/Automation/Automation';
import StyledModal from '../../StyledModal';
import { SideTab } from '../../SideTab';
import Loader from '../../Loader';
import { IndRepository } from '../../../../MedicareProAPI/Repositories/Ind/IndRepository';
import type { AutomationSchedule } from '../../../../MedicareProAPI/Entities/Automation/AutomationSchedule';
import { AutomationScheduleRepository } from '../../../../MedicareProAPI/Repositories/Automation/AutomationScheduleRepository';
import type {
  AutomationCalendarAutomation,
  AutomationCalendarScheduleGroup,
} from '../../../Panels/AgentAutomationSchedulePanel';
import type { Ind } from '../../../../MedicareProAPI/Entities/Ind/Ind';

export interface AutomationScheduleCalendarListItem {
  id: number;
  indId: number;
  ind: Ind;
  automationType: AUTOMATION_ACTION_TYPE;
  processed: boolean;
  total: number;
}

export interface AutomationScheduleCalendarItemProps {
  scheduleItem: AutomationCalendarAutomation;
  userId: number;
  userAccessToken: string;
}

export const AutomationScheduleCalendarItem = (
  props: AutomationScheduleCalendarItemProps
): React.JSX.Element => {
  const [scheduleItem, setScheduleItem] = React.useState(props.scheduleItem);
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [deleteScheduleId, setDeleteScheduleId] = React.useState<number>(null);
  const [deleteModalShow, setDeleteModalShow] = React.useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = React.useState<boolean>(false);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [itemData, setItemData] = React.useState<any[]>(null);

  const individualRepository = new IndRepository();
  individualRepository.setAccessToken(props.userAccessToken);

  const automationScheduleRepository = new AutomationScheduleRepository();
  automationScheduleRepository.setAccessToken(props.userAccessToken);

  const classNames = ['badge', 'text-white', 'text-left', 'font-weight-normal', 'cp'];

  // Work out how many of the scheduled items have been processed
  let processed = 0;

  scheduleItem.groupedSchedules.forEach(
    (groupedSchedule: AutomationCalendarScheduleGroup, index) => {
      groupedSchedule.schedules.forEach((scheduleSingle: AutomationSchedule) => {
        if (scheduleSingle.getProcessed() === true) {
          processed++;
        }
      });
    }
  );

  if (processed === scheduleItem.groupedSchedules.length) {
    classNames.push('bg-success');
  } else if (processed > 0) {
    classNames.push('bg-success');
  } else {
    classNames.push('bg-secondary');
  }

  const deleteAutomationSchedule = (automationScheduleId: number) => {
    setDeleteLoading(true);

    automationScheduleRepository
      .deleteAutomationSchedule(props.userId, automationScheduleId)
      .then((response) => {
        const updatedItemData = [...itemData];
        const automationScheduleIndex = itemData.findIndex(
          (item) => item.id === automationScheduleId
        );

        updatedItemData.splice(automationScheduleIndex, 1);

        setItemData(updatedItemData);
        setDeleteScheduleId(null);
        setDeleteModalShow(false);
        setDeleteLoading(false);
      });
  };

  React.useEffect(() => {
    if (expanded === true && loading === false && itemData === null) {
      setLoading(true);

      const newItemData = [];

      scheduleItem.groupedSchedules.forEach(
        (groupedSchedule: AutomationCalendarScheduleGroup, index) => {
          const scheduleSingle = groupedSchedule.schedules[0];

          const indId = scheduleSingle.getData().ind.indId;

          const itemData: AutomationScheduleCalendarListItem = {
            id: scheduleSingle.getId(),
            indId: indId,
            ind: null,
            automationType: scheduleSingle.getAutomationAction().getType(),
            processed: scheduleSingle.getProcessed(),
            total: groupedSchedule.schedules.length,
          };

          newItemData.push(itemData);
        }
      );

      setItemData(newItemData);

      newItemData.forEach((itemData: any, index) => {
        individualRepository
          .getInd(itemData.indId, { include: ['emails', 'phones', 'addresses'] })
          .then((individual) => {
            const editedItemData = [...newItemData];

            editedItemData[index].ind = individual;

            setItemData(editedItemData);
          });
      });

      setLoading(false);
    }
  }, [expanded]);

  return (
    <>
      <StyledModal
        size="md"
        title="Delete Scheduled Send"
        footerCloseButton={true}
        footerButtons={
          <button
            onClick={(e) => deleteAutomationSchedule(deleteScheduleId)}
            className="btn btn-danger"
            type="button"
          >
            {deleteLoading && <i className="fas fa-spinner fa-spin"></i>} Delete
          </button>
        }
        show={deleteModalShow}
        onClose={(e) => setDeleteModalShow(false)}
      >
        <div className="row pl-3 pr-3">
          <p>Are you sure you want to delete this scheduled send? This cannot be undone.</p>
        </div>
      </StyledModal>
      <SideTab
        size="lg"
        position="right"
        title="Schedule Details"
        onClose={(e) => setExpanded(false)}
        show={expanded}
      >
        {loading && <Loader size="large" className="mt-4 mb-4" />}
        {itemData && itemData.length === 0 && (
          <div className="alert alert-warning mt-3">
            <i className="far fa-info-circle mr-2"></i>
            No further scheduled sends for this day
          </div>
        )}
        {itemData && itemData.length > 0 && (
          <>
            <div className="row px-3">
              <div className="col-sm-3 py-1 px-0">
                <strong>Name</strong>
              </div>
              <div className="col-sm-3 py-1 px-0"></div>
              <div className="col-sm-3 py-1 px-0">
                <strong>Type</strong>
              </div>
              <div className="col-sm-3 py-1 px-0">
                <strong>Options</strong>
              </div>
              {itemData.map((itemDataSingle) => {
                return (
                  <>
                    <div className="col-sm-3 py-1 px-0 border-top">
                      {itemDataSingle.ind ? (
                        itemDataSingle.ind.getIndName()
                      ) : (
                        <Loader size="small" />
                      )}
                    </div>
                    <div className="col-sm-3 py-1 px-0 border-top">
                      {itemDataSingle.ind && (
                        <>
                          {(itemDataSingle.automationType as AUTOMATION_ACTION_TYPE) ===
                            AUTOMATION_ACTION_TYPE.SEND_EMAIL &&
                            (itemDataSingle.ind.getDefaultEmail()
                              ? itemDataSingle.ind.getDefaultEmail().getIndEmailAddress()
                              : 'No email')}
                          {(itemDataSingle.automationType as AUTOMATION_ACTION_TYPE) ===
                            AUTOMATION_ACTION_TYPE.SEND_SMS &&
                            (itemDataSingle.ind.getDefaultPhone()
                              ? itemDataSingle.ind.getDefaultPhone().getIndPhoneNumber()
                              : 'No number')}
                          {(itemDataSingle.automationType as AUTOMATION_ACTION_TYPE) ===
                            AUTOMATION_ACTION_TYPE.SEND_MAIL &&
                            (itemDataSingle.ind.getDefaultAddress()
                              ? [
                                  itemDataSingle.ind.getDefaultAddress().getIndAddressStreet(),
                                  itemDataSingle.ind.getDefaultAddress().getIndAddressCity(),
                                  itemDataSingle.ind.getDefaultAddress().getIndAddressState(),
                                  itemDataSingle.ind.getDefaultAddress().getIndAddressZip(),
                                ]
                                  .filter((address) => address)
                                  .join(', ')
                              : 'No address')}
                        </>
                      )}
                    </div>
                    <div className="col-sm-3 py-1 px-0 border-top">
                      {itemDataSingle.automationType && (
                        <>
                          <i
                            className={`mr-2 far ${AUTOMATION_ACTION_TYPE_ICONS.find((item) => item.value === (itemDataSingle.automationType as AUTOMATION_ACTION_TYPE)).label}`}
                          />
                          {
                            AUTOMATION_ACTION_TYPE_LABELS.find(
                              (item) =>
                                item.value ===
                                (itemDataSingle.automationType as AUTOMATION_ACTION_TYPE)
                            ).label
                          }
                        </>
                      )}
                    </div>
                    <div className="col-sm-3 py-1 px-0 border-top">
                      {!itemDataSingle.processed && (
                        <div
                          onClick={(e) => {
                            setDeleteScheduleId(itemDataSingle.id);
                            setDeleteModalShow(true);
                          }}
                          className="badge small font-weight-normal text-white bg-danger"
                        >
                          Delete
                        </div>
                      )}
                      {itemDataSingle.processed && (
                        <span className="text-success">
                          <i className="far fa-check-circle mr-1 "></i>Processed
                        </span>
                      )}
                    </div>
                  </>
                );
              })}
            </div>
          </>
        )}
      </SideTab>
      <div onClick={(e) => setExpanded(true)} className={classNames.join(' ')}>
        <strong>{scheduleItem.automationName}:</strong>
        <br />
        {itemData ? itemData.length : scheduleItem.groupedSchedules.length} scheduled
      </div>
    </>
  );
};
